import { Directive } from 'vue'
import './drag-scroll.scss'

const VELOCITY_DURATION = 300 // ms for slowdown animation
const VELOCITY_MULTIPLIER = 200 // multiplier for velocity amount

type ScrollDirection = 'scrollTop' | 'scrollLeft'
type ScrollMaxProp = 'scrollHeight' | 'scrollWidth'
type PosProp = 'pageY' | 'pageX'

interface DragScrollOptions {
  vertical?: boolean
  snap?: boolean
  enabled?: boolean
}

interface DragState {
  down: boolean
  active: boolean
}

// Easing function for scroll slowdown animation
function easeOutQuart(x: number): number {
  return 1 - ((1 - x) ** 4)
}

/**
 * Animate the selected element with easeOutQuart
 * @param el {HTMLElement}
 * @param state {Object} Object (by reference) to watch to see if they re-grabbed it, to stop the animation
 * @param endPos {number} Final scroll position
 * @param scrollProp {string} Property to use for scroll (scrollTop / scrollLeft)
 */
function animateTo(el: HTMLElement, state: DragState, endPos: number, scrollProp: ScrollDirection): void {
  const start = performance.now()
  const startPos = el[scrollProp]

  const tick = (now: number) => {
    // If start dragging again, abandon
    if (state.down) {
      return
    }

    // Find destination for this frame
    const progress = (now - start) / VELOCITY_DURATION
    const distance = easeOutQuart(progress) * (endPos - startPos)

    el[scrollProp] = startPos + distance

    if (progress >= 1) {
      // Done
      el.classList.remove('ts-active')
    } else {
      // Request another frame
      requestAnimationFrame(tick)
    }
  }

  requestAnimationFrame(tick)
}

function updateProps(el: HTMLElement, value: DragScrollOptions = { vertical: false, snap: false, enabled: true }): void {
  const data = el.dataset
  data.scrollProp = value.vertical ? 'scrollTop' : 'scrollLeft'
  data.maxProp = value.vertical ? 'scrollHeight' : 'scrollWidth'
  data.posProp = value.vertical ? 'pageY' : 'pageX'
  data.snap = value.snap ? 'true' : ''
  data.enabled = value.enabled !== false ? 'true' : 'false'
}

const dragScrollDirective: Directive<HTMLElement, DragScrollOptions> = {
  beforeMount(el: HTMLElement, binding: { value?: DragScrollOptions }) {
    const value = binding.value ?? { vertical: false, snap: false }

    const state: DragState = {
      down   : false,
      active : false,
    }

    let eventStartPos = 0
    let startPos = 0

    let velocityPos = 0
    let velocityTs = 0
    let velocity = 0

    updateProps(el, value)

    let scrollProp: ScrollDirection = value.vertical ? 'scrollTop' : 'scrollLeft'
    let posProp = value.vertical ? 'pageY' : 'pageX'
    let maxProp = value.vertical ? 'scrollHeight' : 'scrollWidth'
    let snap = value.snap ?? false

    let removeListeners = (_all?: boolean): void => {}

    function updateVelocity(e: MouseEvent): void {
      // Pixels moved per 10ms
      const now = Date.now()
      if (now > velocityTs + 10) {
        // If we have a previous position, we can determine the actual velocity.
        if (velocityPos) {
          velocity = (e[posProp] - velocityPos) / 10
        }
        velocityPos = e[posProp]
        velocityTs = now
      }
    }

    const stop = (e: MouseEvent): void => {
      updateVelocity(e)
      let velocityDelta = velocity * VELOCITY_MULTIPLIER

      // Reset
      state.active = false
      state.down = false

      velocity = 0
      velocityPos = 0
      velocityTs = 0
      removeListeners(false)

      // We will modify this with velocity and snapping
      let endPos = el[scrollProp]

      const elWidth = el.clientWidth

      // Only let them velocity swipe one slide if snap mode
      if (snap) {
        velocityDelta = Math.min(elWidth / 2, Math.max((-elWidth / 2), velocityDelta))
      }

      // Add velocity to the end of the drag
      if (Math.abs(velocityDelta) > 50) {
        endPos -= velocityDelta
      }

      if (snap && posProp === 'pageX') {
        // Determine the leftmost visible slide. If that slide is more than halfway off the screen,
        // go to the next one. Otherwise snap back.
        const remainder = endPos % elWidth
        const leftSlide = Math.floor(endPos / elWidth)
        if (remainder > elWidth / 2) {
          endPos = (leftSlide + 1) * elWidth
        } else {
          endPos = leftSlide * elWidth
        }
      }
      // Dont allow over/under scroll
      endPos = Math.max(0, Math.min(el[maxProp], endPos))

      if (Math.abs(endPos - el[scrollProp]) > 25) {
        animateTo(el, state, endPos, scrollProp)
      } else {
        el.classList.remove('ts-active')
      }
    }

    const drag = (e) => {
      e.preventDefault()

      // Total distance dragged
      const distance = e[posProp] - eventStartPos

      if (Math.abs(distance) > 10) {
        state.active = true
        el.classList.add('ts-active')
      }

      if (!state.active) return

      updateVelocity(e)

      el[scrollProp] = startPos - distance
    }

    const start = (e) => {
      // Prevent if dragging scrollbar. Must drag element inside.
      if (e.target === el && !el.classList.contains('ts-active')) {
        if (value.vertical && (e.clientX > el.getBoundingClientRect().right - 45)) {
          return
        }
        if (!value.vertical && (e.clientY > el.getBoundingClientRect().bottom - 45)) {
          return
        }
      }

      // In case we changed properties, reload the local variables
      const config = el.dataset
      scrollProp = config.scrollProp as ScrollDirection
      maxProp = config.maxProp as ScrollMaxProp
      posProp = config.posProp as PosProp
      snap = config.snap === 'true'

      if (config.enabled === 'false') {
        return
      }

      window.addEventListener('mousemove', drag)
      window.addEventListener('mouseup', stop)
      window.addEventListener('mouseleave', stop)

      state.down = true

      e.preventDefault()

      eventStartPos = e[posProp]
      startPos = el[scrollProp]
    }

    const touchStart = () => removeListeners(true)

    /**
     * @param all {boolean} Remove the start / mousedown as well (for destruction or using touch)
     */
    removeListeners = (all?: boolean): void => {
      if (all === true) {
        el.removeEventListener('touchstart', touchStart)
        el.removeEventListener('mousedown', start)
      }

      window.removeEventListener('mousemove', drag)
      window.removeEventListener('mouseup', stop)
      window.removeEventListener('mouseout', stop)
    }

    // Save the unbind function to the element for reference later
    el._removeListeners = removeListeners
    el.addEventListener('touchstart', removeListeners, { passive: true })
    // el.addEventListener('click', stop)
    el.addEventListener('mousedown', start)
  },

  updated(el: HTMLElement, binding: { value?: DragScrollOptions }): void {
    updateProps(el, binding.value ?? {})
  },

  unmounted(el: HTMLElement): void {
    el._removeListeners?.(true)
  },
}

export default dragScrollDirective